import React, { useState } from 'react';
import { RecentFile } from '../../hooks/useRecentFiles';

type RecentFilesPanelProps = {
  recentFiles: RecentFile[];
  activeFile: string | null;
  onOpenFile: (path: string) => void;
  onRemoveFile: (path: string) => void;
  onClearAll: () => void;
};

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'ts':
    case 'tsx':
      return '📘';
    case 'js':
    case 'jsx':
      return '📒';
    case 'css':
    case 'scss':
    case 'sass':
      return '🎨';
    case 'html':
      return '🌐';
    case 'json':
      return '📋';
    case 'md':
      return '📝';
    case 'svg':
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return '🖼️';
    default:
      return '📄';
  }
}

export const RecentFilesPanel = React.memo(function RecentFilesPanel({
  recentFiles,
  activeFile,
  onOpenFile,
  onRemoveFile,
  onClearAll,
}: RecentFilesPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);

  if (recentFiles.length === 0) {
    return null;
  }

  return (
    <div className="recent-files-panel">
      <div
        className="recent-files-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
        <span className="recent-files-title">Recent Files</span>
        <span className="recent-files-count">{recentFiles.length}</span>
        {!isCollapsed && recentFiles.length > 0 && (
          <button
            className="recent-files-clear"
            onClick={(e) => {
              e.stopPropagation();
              onClearAll();
            }}
            title="Clear all recent files"
          >
            ✕
          </button>
        )}
      </div>
      {!isCollapsed && (
        <div className="recent-files-list">
          {recentFiles.map((file) => (
            <div
              key={file.path}
              className={`recent-file-item ${activeFile === file.path ? 'active' : ''}`}
              onClick={() => onOpenFile(file.path)}
              onMouseEnter={() => setHoveredPath(file.path)}
              onMouseLeave={() => setHoveredPath(null)}
              title={file.path}
            >
              <span className="recent-file-icon">{getFileIcon(file.name)}</span>
              <span className="recent-file-name">{file.name}</span>
              <span className="recent-file-time">{formatRelativeTime(file.timestamp)}</span>
              {hoveredPath === file.path && (
                <button
                  className="recent-file-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(file.path);
                  }}
                  title="Remove from recent"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
